import { CheckCompany, CheckForm, CheckSignatory } from "winslow-core";

const CHECK_REPORTING_AGENT_NAME = {
    legal_name: "Check Technologies, Inc.",
    first_name: "James",
    last_name: "Kohl",
    middle_name: "",
    email: "Email",
    title: "",
    phone: "856-393-6486",
    fax: "415-484-7068",
    ein: "83-4490700",
    ptin: "",
    address: {
        line1: "228 Park Ave S, PMB 14961",
        line2: "",
        city: "New York",
        state: "NY",
        postal_code: "10003",
        country: "US",
    }
}

// for an input date of YYYY-MM-DD, return the month # for the last month of the quarter
// and the YYYYY in the format YYYYMM   
const getQuarterEndYearMonth = (startDate: string) => {
    const year = startDate.split("-")[0];
    const month = parseInt(startDate.split("-")[1]);
    const quarter = Math.ceil(month / 3);
    const lastMonth = quarter * 3;
    return year + String(lastMonth).padStart(2, "0");
}

// format YYYY-MM-DD to YYYY
const getYYYYofStartDate = (startDate: string) => {
    return startDate.split("-")[0];
}

// input format is YYYY-MM-DD, returns  in the format "mm/dd/yyyy"   
const getDateFormat = (date: string) => {
    const year = date.split("-")[0];
    const month = parseInt(date.split("-")[1]);
    const day = parseInt(date.split("-")[2]);
    return `${month}/${day}/${year}`;
}

const getMonthName = (month: number) => {
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return monthNames[month - 1];
}

// return the date in the format "mm/dd/yyyy"
const getNormalDate = (date: Date) => {
    return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}


function getCurrentIPAddress(): string {
    // get current ip address from browser
    return "127.0.0.1";
}

// returns undefined if we don't have anything so we need to get it from the user
// note that "" is a valid response to a lot of fields
export const checkFormDefault = (
    name: string, 
    checkCompany: CheckCompany,
    checkSignatory: CheckSignatory,
    ein: string,
) : string | undefined => {
    switch (name) {
        case "company_legal_name":
            return checkCompany.legal_name;
        case "company_trade_name":
            return checkCompany.trade_name;
        case "company_federal_ein":
            return ein;
        case "company_address_line1":
            return checkCompany.address?.line1 || "";
        case "company_address_line2":
            return checkCompany.address?.line2 || "";
        case "company_address_city":
            return checkCompany.address?.city || "";
        case "company_address_state":
            return checkCompany.address?.state || "";
        case "company_address_postal_code":
            return checkCompany.address?.postal_code || "";
        case "company_address_country":
            return checkCompany.address?.country || "";
        case "company_business_type":
            return checkCompany.business_type;
        case "company_is_seasonal_employer":
            return "no";
        case "company_contact_person":
            return checkSignatory.first_name + " " + checkSignatory.last_name;
        case "company_contact_person_first_name":
            return checkSignatory.first_name;
        case "company_contact_person_last_name":
            return checkSignatory.last_name;
        case "company_contact_person_initials":
            return checkSignatory.first_name.charAt(0).toUpperCase() + checkSignatory.last_name.charAt(0).toUpperCase();
        case "company_contact_person_uppercase":
            return checkSignatory.first_name.toUpperCase() + " " + checkSignatory.last_name.toUpperCase();
        case "company_contact_phone_number":
            return checkCompany.phone;
        case "company_contact_email":
            return checkSignatory.email;
        case "company_start_quarter_end_year_month":
            return getQuarterEndYearMonth(checkCompany.start_date);
        case "company_filing_authorization_start_date":
            return getDateFormat(checkCompany.start_date);
        case "company_filing_authorization_end_date":
            return "";
        case "reporting_agent_legal_name":
            return CHECK_REPORTING_AGENT_NAME.legal_name;
        case "reporting_agent_federal_ein":
            return CHECK_REPORTING_AGENT_NAME.ein;
        case "reporting_agent_federal_ptin":
            return CHECK_REPORTING_AGENT_NAME.ptin;
        case "reporting_agent_address_line1":
            return CHECK_REPORTING_AGENT_NAME.address?.line1 || "";
        case "reporting_agent_address_line2":
            return CHECK_REPORTING_AGENT_NAME.address?.line2 || "";
        case "reporting_agent_address_city":
            return CHECK_REPORTING_AGENT_NAME.address?.city || "";
        case "reporting_agent_address_state":
            return CHECK_REPORTING_AGENT_NAME.address?.state || "";
        case "reporting_agent_address_postal_code":
            return CHECK_REPORTING_AGENT_NAME.address?.postal_code || "";
        case "reporting_agent_contact_person_first_name":
            return CHECK_REPORTING_AGENT_NAME.first_name;
        case "reporting_agent_contact_person_last_name":
            return CHECK_REPORTING_AGENT_NAME.last_name;
        case "reporting_agent_contact_person":
            return CHECK_REPORTING_AGENT_NAME.first_name + " " + CHECK_REPORTING_AGENT_NAME.last_name;
        case "reporting_agent_contact_person_2_first_name":
            return "";
        case "reporting_agent_contact_person_2_last_name":
            return "";
        case "reporting_agent_contact_person_2":
            return "";
        case "reporting_agent_federal_ptin_2":
            return "";
        case "reporting_agent_signature_title_2":
            return "";
        case "reporting_agent_contact_phone_number_2":
            return "";
        case "reporting_agent_contact_fax_number_2":
            return "";
        case "reporting_agent_contact_email_2":
            return "";
        case "reporting_agent_has_form_944_filing_requirement":
            return "false";
        case "reporting_agent_special_company_tax_category":
            return "not_applicable";
        case "company_signature":
            return checkSignatory.first_name + " " + checkSignatory.last_name;
        case "company_signature_title":
            return checkSignatory.title;
        case "company_signature_date":
            return getNormalDate(new Date());
        case "company_signature_date_month_name":
            return getMonthName(parseInt(checkCompany.start_date.split("-")[1]));
        case "company_signature_timestamp":
            return new Date().toISOString();
        case "company_signature_ip":
            return getCurrentIPAddress();
        case "reporting_agent_signature":
            return CHECK_REPORTING_AGENT_NAME.first_name + " " + CHECK_REPORTING_AGENT_NAME.last_name;
        case "reporting_agent_signature_title":
            return CHECK_REPORTING_AGENT_NAME.title;
        case "reporting_agent_signature_date":
            return getNormalDate(new Date());
        default:
            return undefined;
    }
}

export const checkGenerateFormDefaults = (
    checkForm: CheckForm,
    checkCompany: CheckCompany,
    checkSignatory: CheckSignatory,
    ein: string,
) : {name: string, value: string}[] => {
    const defaults: {name: string, value: string}[] = [];
   
    if (checkForm.parameters) {
        for (const parameter of checkForm.parameters) {
            const value = checkFormDefault(parameter.name, checkCompany, checkSignatory, ein);
            if (value !== undefined) {
                defaults.push({name: parameter.name, value: value});
            }
        }
    }
    return defaults;
}

export const checkGenerateCompanyFormDefaults = (
    checkForm: CheckForm,
    checkCompany: CheckCompany,
    checkSignatory: CheckSignatory,
    ein: string,
): { name: string, value: string }[] => {
    switch (checkForm.description) {
        case "Federal Form 8655":
            return [
                { name: "company_legal_name", value: checkCompany.legal_name},
                { name: "company_trade_name", value: checkCompany.trade_name},
                { name: "company_federal_ein", value: ein},
                { name: "company_address_line1", value: checkCompany.address?.line1 || ""},
                { name: "company_address_line2", value: checkCompany.address?.line2 || ""},
                { name: "company_address_city", value: checkCompany.address.city || ""},
                { name: "company_address_state", value: checkCompany.address.state || ""},
                { name: "company_address_postal_code", value: checkCompany.address.postal_code || ""},
                { name: "company_address_country", value: checkCompany.address.country || ""},
                { name: "company_is_seasonal_employer", value: "no"},
                { name: "company_contact_person", value: checkSignatory.first_name + " " + checkSignatory.last_name},
                { name: "company_contact_person_first_name", value: checkSignatory.first_name},
                { name: "company_contact_person_last_name", value: checkSignatory.last_name},
                { name: "company_business_type", value: checkCompany.business_type},
                { name: "company_contact_person_initials", value: checkSignatory.first_name.charAt(0).toUpperCase() + checkSignatory.last_name.charAt(0).toUpperCase()},
                { name: "company_contact_person_uppercase", value: checkSignatory.first_name.toUpperCase() + " " + checkSignatory.last_name.toUpperCase()},
                { name: "company_contact_phone_number", value: checkCompany.phone},
                { name: "company_contact_email", value: checkSignatory.email},
                { name: "company_start_quarter_end_year_month", value: getQuarterEndYearMonth(checkCompany.start_date)},
                { name: "company_filing_authorization_start_date", value: getDateFormat(checkCompany.start_date)},
                { name: "company_filing_authorization_end_date", value: ""},
                { name: "reporting_agent_legal_name", value: CHECK_REPORTING_AGENT_NAME.legal_name},
                { name: "reporting_agent_federal_ein", value: CHECK_REPORTING_AGENT_NAME.ein},
                { name: "reporting_agent_federal_ptin", value: CHECK_REPORTING_AGENT_NAME.ptin},
                { name: "reporting_agent_address_line1", value: CHECK_REPORTING_AGENT_NAME.address.line1 || ""},
                { name: "reporting_agent_address_line2", value: CHECK_REPORTING_AGENT_NAME.address.line2 || ""},
                { name: "reporting_agent_address_city", value: CHECK_REPORTING_AGENT_NAME.address.city || ""},
                { name: "reporting_agent_address_state", value: CHECK_REPORTING_AGENT_NAME.address.state || ""},
                { name: "reporting_agent_address_postal_code", value: CHECK_REPORTING_AGENT_NAME.address.postal_code || ""},
                { name: "reporting_agent_contact_person_first_name", value: CHECK_REPORTING_AGENT_NAME.first_name},
                { name: "reporting_agent_contact_person_last_name", value: CHECK_REPORTING_AGENT_NAME.last_name},
                { name: "reporting_agent_contact_person", value: CHECK_REPORTING_AGENT_NAME.first_name + " " + CHECK_REPORTING_AGENT_NAME.last_name},
                { name: "reporting_agent_contact_person_2_first_name", value: ""},
                { name: "reporting_agent_contact_person_2_last_name", value: ""},
                { name: "reporting_agent_contact_person_2", value: ""},
                { name: "reporting_agent_federal_ptin_2", value: ""},
                { name: "reporting_agent_signature_title_2", value: ""},
                { name: "reporting_agent_contact_phone_number_2", value: ""},
                { name: "reporting_agent_contact_fax_number_2", value: ""},
                { name: "reporting_agent_contact_email_2", value: ""},
                { name: "reporting_agent_has_form_944_filing_requirement", value: "false"},
                { name: "reporting_agent_special_company_tax_category", value: "not_applicable"},
                { name: "company_signature", value: checkSignatory.first_name + " " + checkSignatory.last_name},
                { name: "company_signature_title", value: checkSignatory.title},
                { name: "company_signature_date", value: getDateFormat(checkCompany.start_date)},
                { name: "company_signature_date_month_name", value: getMonthName(parseInt(checkCompany.start_date.split("-")[1]))},
                { name: "company_signature_timestamp", value: new Date().toISOString()},
                { name: "company_signature_ip", value: getCurrentIPAddress()},
                { name: "reporting_agent_signature", value: checkSignatory.first_name + " " + checkSignatory.last_name},
                { name: "reporting_agent_signature_title", value: checkSignatory.title},
                { name: "reporting_agent_signature_date", value: getNormalDate(new Date())},
            ];
        default:
            return [];
    }
}

const CHECK_RENDER_PARAMETERS = [
    "california_employee_withholding_ein"
]

const CHECK_PARAMETER_FORMATS = [
    {
        name: "california_employee_withholding_ein",
        formats: ["###-####-#", "########"],
    },
]

export const checkShouldIRenderFormParameter = (checkForm: CheckForm, name: string) => {
    return CHECK_RENDER_PARAMETERS.includes(name);
}

export const checkParameterFormat = (name: string) : string[] | undefined => {
    for (const format of CHECK_PARAMETER_FORMATS) {
        if (format.name === name) {
            return format.formats;
        }
    }
    return undefined;
}
